import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Copy, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/segmented";
import Sparkline from "@/components/Vitals/Sparkline";
import { formatDistanceToNowStrict, isValid } from "date-fns";
import { formatDate } from "@/lib/formatDate";
import { repeatLastMedicines } from "@/lib/repeatMedicines";
import { examSigns } from "@/lib/vitals";
import type { Prescription } from "@/types/prescription";

type ContextVisit = { id: string; date: string; prescription?: Prescription };

interface ContextPanelProps {
  /** Newest first */
  visits: ContextVisit[];
  onViewPrescription: (prescription: Prescription) => void;
}

const num = (v: unknown) => {
  const n = parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : null;
};

const medLine = (m: NonNullable<Prescription["medicines"]>[number]) =>
  [m.dosage, m.taken_when, m.duration].filter(Boolean).join(" · ");

/**
 * The prescription pad's right column: what happened last time and how things are trending,
 * beside the form instead of below it. Sits inside the form's FormProvider so it can copy medicines in.
 */
export default function ContextPanel({ visits, onViewPrescription }: ContextPanelProps) {
  const [tab, setTab] = useState<"last" | "history">("last");
  const { getValues, setValue } = useFormContext();
  const withRx = visits.filter(v => v.prescription);
  const last = withRx[0]?.prescription;
  const lastExamination = [examSigns(last?.vitals), last?.examination?.trim()].filter(Boolean).join("\n");

  // Oldest → newest readings for the trend lines
  const trends = useMemo(() => {
    const chrono = [...withRx].reverse().map(v => v.prescription!.vitals ?? {});
    const series = (pick: (v: any) => number | null) => chrono.map(pick).filter((n): n is number => n != null);
    const bp = chrono.map(v => String(v.bp ?? "").match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/)).filter(Boolean) as RegExpMatchArray[];
    return [
      { label: "BP", unit: "mmHg", series: [bp.map(m => +m[1]), bp.map(m => +m[2])], latest: bp.length ? `${bp[bp.length - 1][1]}/${bp[bp.length - 1][2]}` : null },
      { label: "Pulse", unit: "bpm", series: [series(v => num(v.pulse))] },
      { label: "Weight", unit: "kg", series: [series(v => num(v.weight))] },
      { label: "SpO₂", unit: "%", series: [series(v => num(v.spo2))] },
    ]
      .map(t => ({ ...t, latest: t.latest ?? (t.series[0].length ? String(t.series[0][t.series[0].length - 1]) : null) }))
      .filter(t => t.series[0].length >= 2);
  }, [withRx]);

  const copyMedicines = () => repeatLastMedicines(last, getValues, setValue);

  return (
    <aside aria-label="Patient context" className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-2 px-3 h-11 border-b border-border">
        <Segmented
          aria-label="Context"
          value={tab}
          onChange={(v) => setTab(v as "last" | "history")}
          options={[
            { value: "last", label: "Last visit" },
            { value: "history", label: `History (${withRx.length})` },
          ]}
        />
      </div>

      {tab === "last" && (
        !last ? (
          <p className="px-3 py-6 text-sm text-muted-foreground text-center">First visit — nothing to compare yet.</p>
        ) : (
          <div className="divide-y divide-border">
            <div className="px-3 py-3 space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-[15px] font-semibold text-foreground tabular-nums">
                  {formatDate(last.date)}
                  {isValid(new Date(last.date)) && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {formatDistanceToNowStrict(new Date(last.date), { addSuffix: true })}
                    </span>
                  )}
                </p>
                <button type="button" onClick={() => onViewPrescription(last)} className="text-xs font-medium text-primary hover:underline">
                  View
                </button>
              </div>
              {last.diagnosis && <p className="text-sm font-semibold text-foreground">{last.diagnosis}</p>}
              {!!last.complaints?.length && (
                <p className="text-xs text-muted-foreground">
                  {last.complaints.map(c => [c.complaint, c.duration].filter(Boolean).join(" ")).filter(Boolean).join(", ")}
                </p>
              )}
            </div>

            {lastExamination && (
              <div className="px-3 py-3">
                <h3 className="text-xs text-muted-foreground mb-1">Examination</h3>
                <p className="text-sm text-foreground/90 whitespace-pre-line line-clamp-4">{lastExamination}</p>
              </div>
            )}

            {!!last.medicines?.length && (
              <div className="px-3 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-xs text-muted-foreground">Medicines</h3>
                  <span className="text-2xs text-muted-foreground tabular-nums">{last.medicines.length}</span>
                </div>
                <ul className="space-y-1.5">
                  {last.medicines.filter(m => m.product?.trim()).map((m, i) => (
                    <li key={i} className="text-sm leading-5">
                      <span className="font-medium text-foreground">{m.product}</span>
                      {medLine(m) && <span className="block text-xs text-muted-foreground">{medLine(m)}</span>}
                    </li>
                  ))}
                </ul>
                <Button type="button" variant="outline" size="sm" onClick={copyMedicines} className="mt-3 w-full gap-1.5">
                  <Copy className="w-3.5 h-3.5" /> Copy medicines to this prescription
                </Button>
              </div>
            )}

            {last.advice && (
              <div className="px-3 py-3">
                <h3 className="text-xs text-muted-foreground mb-1">Advice</h3>
                <p className="text-sm text-foreground/90 whitespace-pre-line line-clamp-4">{last.advice}</p>
              </div>
            )}

            {trends.length > 0 && (
              <div className="px-3 py-3">
                <h3 className="text-[13px] font-semibold text-foreground mb-2">Trend · last {withRx.length} visits</h3>
                <div className="space-y-2">
                  {trends.map(t => (
                    <div key={t.label} className="grid grid-cols-[4.5rem_minmax(0,1fr)_5.5rem] items-center gap-2">
                      <span className="text-[13px] text-label">{t.label}</span>
                      <Sparkline series={t.series} />
                      <span className="text-right text-sm font-mono font-semibold tabular-nums text-foreground">
                        {t.latest} <span className="text-2xs font-normal text-muted-foreground">{t.unit}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {tab === "history" && (
        <ul className="divide-y divide-border max-h-[32rem] overflow-y-auto">
          {withRx.length === 0 && <li className="px-3 py-6 text-sm text-muted-foreground text-center">No earlier visits.</li>}
          {withRx.map(v => {
            const rx = v.prescription!;
            const meds = (rx.medicines ?? []).map(m => m.product).filter(Boolean);
            return (
              <li key={v.id} className="px-3 py-2.5 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground tabular-nums">{formatDate(rx.date || v.date)}</p>
                  <p className="text-sm font-medium text-foreground truncate">{rx.diagnosis || "No diagnosis recorded"}</p>
                  {meds.length > 0 && <p className="text-xs text-muted-foreground truncate" title={meds.join(", ")}>{meds.join(", ")}</p>}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => onViewPrescription(rx)} className="h-8 w-8 p-0 flex-shrink-0" aria-label={`View prescription from ${formatDate(rx.date || v.date)}`}>
                  <Eye className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
